import CssBaseline from '@mui/material/CssBaseline';
import { createTheme, ThemeOptions, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { merge } from 'lodash';
import { useMemo } from 'react';

import { useSettingsContext } from 'src/components/settings';
import { Palette } from 'src/types/whitelabel';

import { customShadows } from './custom-shadows';
import { createContrast } from './options/contrast';
// options
import RTL from './options/right-to-left';
import { componentsOverrides } from './overrides';
// system
import { palette } from './palette';
import { shadows } from './shadows';
import { typography } from './typography';

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

export default function ThemeProvider({ children }: Props) {
  const settings = useSettingsContext();

  const paletteValue = palette(settings.themeMode) as unknown as Palette;

  const contrast = createContrast(paletteValue, settings.themeContrast, settings.themeMode);

  const memoizedValue = useMemo(
    () => ({
      palette: {
        ...palette(settings.themeMode),
        ...contrast.palette,
      },
      customShadows: {
        ...customShadows(paletteValue, settings.themeMode),
      },
      direction: settings.themeDirection,
      shadows: shadows(paletteValue, settings.themeMode),
      shape: { borderRadius: 8 },
      typography,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [settings.themeMode, settings.themeDirection, contrast.palette]
  );

  const theme = createTheme(memoizedValue as ThemeOptions);

  theme.components = merge(componentsOverrides(theme), contrast.components);

  return (
    <MuiThemeProvider theme={theme}>
      <RTL themeDirection={settings.themeDirection}>
        <CssBaseline />
        {children}
      </RTL>
    </MuiThemeProvider>
  );
}
